import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { fmtMoney, fmtVin, fmtDate, vehicleLabel } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import VehicleFormDrawer from "@/components/VehicleFormDrawer";
import VehicleCsvIO from "@/components/VehicleCsvIO";
import BranchFilter from "@/components/BranchFilter";
import ModelCategoryFilter from "@/components/ModelCategoryFilter";
import VehicleTransferDialog from "@/components/VehicleTransferDialog";
import TransferHistoryDrawer from "@/components/TransferHistoryDrawer";
import useBranches from "@/hooks/useBranches";
import {
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Trash,
  ArrowsLeftRight,
  ClockCounterClockwise,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { VEHICLE_TYPES, VEHICLE_STATUSES } from "@/lib/constants";

function VehicleRow({ vehicle, packageMap, branchMap, showBranch, isAdmin, selected, onToggleSelect, onEdit, onDelete, onCloseAd, onReopenAd, isAdBranch }) {
  const typeMeta = VEHICLE_TYPES.find((t) => t.value === vehicle.type);
  const TypeIcon = typeMeta?.icon;
  const isPending = !!vehicle.pending_transfer_id;
  const selectable = vehicle.status === "available" && !isPending;
  const attachedPackages = (vehicle.package_ids || [])
    .map((id) => packageMap[id])
    .filter(Boolean);
  const pkgTotal = attachedPackages.reduce((acc, p) => acc + (p.price || 0), 0);
  // Margin reflects vehicle profit only — Ex-showroom (no tax) less purchase cost. Tax is
  // pass-through to the government and add-ons (insurance, etc.) belong to their vendors.
  const exShowroomTotal = attachedPackages.reduce((acc, p) => {
    const ex = (p.items || []).find((i) => i.label === "Ex-showroom (no tax)");
    return acc + (Number(ex?.price) || 0);
  }, 0);
  const customerPrice = pkgTotal;
  const margin = exShowroomTotal > 0 ? exShowroomTotal - (vehicle.price || 0) : 0;
  // Stock age — computed from `purchased_on` (CSV-imported) or fallback to created_at.
  // Wrapped in useMemo to keep the render function deterministic (Date.now is
  // an impure side-input — react-hooks/purity flags it inline).
  const stockBaseDate = vehicle.purchased_on || vehicle.created_at;
  const stockAgeDays = useMemo(() => {
    if (!stockBaseDate) return null;
    const dt = new Date(stockBaseDate);
    if (Number.isNaN(dt.getTime())) return null;
    return Math.max(0, Math.floor((Date.now() - dt.getTime()) / 86400000));
  }, [stockBaseDate]);
  return (
    <tr
      data-testid={`vehicle-row-${vehicle.id}`}
      className={`border-b border-canvas-line transition-colors duration-150 ${
        selected ? "bg-brand/5" : "hover:bg-canvas-panel"
      }`}
    >
      <td className="px-4 py-3 w-10">
        <input
          type="checkbox"
          data-testid={`select-vehicle-${vehicle.id}`}
          checked={selected}
          disabled={!selectable}
          onChange={() => onToggleSelect(vehicle)}
          aria-label="Select for transfer"
          title={
            selectable
              ? "Select for transfer"
              : isPending
              ? "Vehicle is pending another transfer"
              : "Only available vehicles can be transferred"
          }
          className="cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
        />
      </td>
      <td className="px-4 py-3">
        <div className="text-sm text-ink" data-testid={`vehicle-name-${vehicle.id}`}>
          {(() => {
            // Prefer the linked package name(s) — that's the operationally
            // meaningful variant string (e.g. "IQUBE S15-Beige"). Fall back
            // to the hydrated package_name / model_category for legacy rows
            // that have no packages attached.
            const pkgNames = (vehicle.packages || [])
              .map((p) => p && p.name)
              .filter(Boolean);
            if (pkgNames.length) return pkgNames.join(" · ");
            return vehicleLabel(vehicle) || "—";
          })()}
        </div>
        <span
          className="inline-flex items-center gap-1.5 mt-1 px-2 py-0.5 border border-canvas-line text-[10px] font-mono uppercase tracking-wider text-ink-muted"
          data-testid={`vehicle-type-${vehicle.id}`}
        >
          {TypeIcon && <TypeIcon size={10} weight="bold" />}
          {vehicle.type}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-canvas-panel border border-canvas-line overflow-hidden shrink-0">
            {vehicle.image_url ? (
              <img src={vehicle.image_url} alt="" className="w-full h-full object-cover" />
            ) : (
              TypeIcon && (
                <div className="w-full h-full flex items-center justify-center">
                  <TypeIcon size={18} weight="bold" />
                </div>
              )
            )}
          </div>
          <div className="min-w-0">
            {attachedPackages.length > 0 ? (
              <div
                className="text-sm font-semibold text-ink truncate"
                title={attachedPackages.map((p) => p.name).join(" · ")}
                data-testid={`vehicle-packages-${vehicle.id}`}
              >
                {attachedPackages.map((p) => p.name).join(" · ")}
              </div>
            ) : (
              <div className="text-sm text-ink-subtle italic">
                No package
              </div>
            )}
            <div className="font-mono text-[11px] text-ink-muted">
              {vehicle.color || "—"} · {vehicle.mileage || 0} mi
            </div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 font-mono text-xs text-ink">{fmtVin(vehicle.vin)}</td>
      <td
        className="px-4 py-3 font-mono text-[11px] text-ink"
        data-testid={`vehicle-dealer-code-${vehicle.id}`}
      >
        {vehicle.dealer_code ?? <span className="text-ink-subtle">—</span>}
      </td>
      {showBranch && (
        <td
          className="px-4 py-3 font-mono text-[11px] text-ink-muted"
          data-testid={`vehicle-branch-${vehicle.id}`}
        >
          {branchMap[vehicle.branch_id]?.name || "—"}
        </td>
      )}
      <td
        className="px-4 py-3 font-mono text-sm text-ink-muted"
        title="Internal — what the dealer paid"
      >
        {fmtMoney(vehicle.price)}
      </td>
      <td
        className="px-4 py-3 font-mono text-sm text-ink"
        data-testid={`customer-price-${vehicle.id}`}
      >
        {pkgTotal > 0 ? (
          <>
            <span className="font-semibold">{fmtMoney(customerPrice)}</span>
            {margin !== 0 && (
              <div
                className={`text-[10px] ${
                  margin > 0 ? "text-[#00B341]" : "text-destructive"
                }`}
              >
                {margin > 0 ? "+" : ""}
                {fmtMoney(margin)} margin
              </div>
            )}
          </>
        ) : (
          <span className="text-ink-subtle">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={vehicle.status} />
        {isPending && (
          <div
            data-testid={`pending-badge-${vehicle.id}`}
            className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#B58900]/10 text-[#B58900] font-mono text-[9px] uppercase tracking-wider"
            title="Pending inter-branch transfer approval"
          >
            ⏳ Pending transfer
          </div>
        )}
      </td>
      <td className="px-4 py-3 font-mono text-[11px] text-ink-muted">
        <div data-testid={`vehicle-stock-age-${vehicle.id}`}>
          <div>{fmtDate(stockBaseDate)}</div>
          {stockAgeDays != null && (
            <div className="text-[10px] text-ink-subtle">
              {stockAgeDays === 0 ? "Today" : `${stockAgeDays}d in stock`}
            </div>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1 justify-end">
          {isAdBranch && vehicle.status === "available" && (
            <button
              onClick={() => onCloseAd(vehicle)}
              data-testid={`close-ad-${vehicle.id}`}
              title="Mark as sold by AD (removes from AD stock, no accounting entry)"
              className="px-2 py-1 border border-emerald-400 bg-emerald-50 text-emerald-800 hover:border-emerald-600 text-[10px] font-mono uppercase tracking-wider whitespace-nowrap"
            >
              Close (AD Sold)
            </button>
          )}
          {isAdBranch && vehicle.status === "sold_by_ad" && (
            <button
              onClick={() => onReopenAd(vehicle)}
              data-testid={`reopen-ad-${vehicle.id}`}
              title="Undo the AD-sold marker — vehicle becomes available in AD stock again"
              className="px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase tracking-wider hover:border-ink"
            >
              Reopen
            </button>
          )}
          <button
            onClick={() => onEdit(vehicle)}
            data-testid={`edit-vehicle-${vehicle.id}`}
            className="p-1.5 border border-canvas-line hover:border-ink transition-colors"
            aria-label="Edit"
          >
            <PencilSimple size={13} weight="bold" />
          </button>
          {isAdmin && (
            <button
              onClick={() => onDelete(vehicle)}
              data-testid={`delete-vehicle-${vehicle.id}`}
              className="p-1.5 border border-canvas-line hover:border-destructive hover:text-destructive transition-colors"
              aria-label="Delete"
            >
              <Trash size={13} weight="bold" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function InventoryFilters({ search, setSearch, filterType, setFilterType, filterStatus, setFilterStatus, filterBranch, setFilterBranch, filterDealerCode, setFilterDealerCode, filterModel, setFilterModel, dealerCodes, onSearchSubmit }) {
  return (
    <div className="bg-canvas-card border border-canvas-line p-4 flex flex-wrap items-center gap-3 mb-6">
      <div className="flex-1 min-w-[200px] relative">
        <MagnifyingGlass
          size={14}
          weight="bold"
          className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted"
        />
        <input
          data-testid="inventory-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSearchSubmit()}
          placeholder="Search VIN, engine no, package…"
          className="w-full pl-9 pr-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
        />
      </div>

      <BranchFilter value={filterBranch} onChange={setFilterBranch} testId="inventory-branch-filter" kind="sales" />
      <ModelCategoryFilter value={filterModel} onChange={setFilterModel} testId="inventory-model-filter" />

      <div className="flex items-center gap-1 border border-canvas-line bg-canvas">
        <button
          data-testid="filter-type-all"
          onClick={() => setFilterType("")}
          className={`px-3 py-2 text-xs font-mono uppercase tracking-wider ${
            filterType === "" ? "bg-ink text-white" : "text-ink"
          }`}
        >
          All
        </button>
        {VEHICLE_TYPES.map((t) => {
          const Icon = t.icon;
          const active = filterType === t.value;
          return (
            <button
              key={t.value}
              data-testid={`filter-type-${t.value}`}
              onClick={() => setFilterType(t.value)}
              className={`px-3 py-2 text-xs font-mono uppercase tracking-wider flex items-center gap-1.5 ${
                active ? "bg-ink text-white" : "text-ink"
              }`}
            >
              <Icon size={12} weight="bold" />
              {t.label}
            </button>
          );
        })}
      </div>

      <select
        data-testid="filter-status"
        value={filterStatus}
        onChange={(e) => setFilterStatus(e.target.value)}
        className="px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
      >
        <option value="">Stock only (Available + Reserved)</option>
        <option value="all">Show all (incl. sold & registered)</option>
        {VEHICLE_STATUSES.map((s) => (
          <option key={s} value={s}>
            {s[0].toUpperCase() + s.slice(1)}
          </option>
        ))}
      </select>

      {dealerCodes.length > 0 && (
        <select
          data-testid="filter-dealer-code"
          value={filterDealerCode}
          onChange={(e) => setFilterDealerCode(e.target.value)}
          className="px-3 py-2 bg-canvas border border-canvas-line text-sm font-mono focus:outline-none focus:border-ink"
          title="Filter by TVS dealer code"
        >
          <option value="">All dealer codes</option>
          {dealerCodes.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

function InventoryTableBody({ loading, vehicles, packageMap, branchMap, showBranch, isAdmin, selectedIds, onToggleSelect, onEdit, onDelete, onCloseAd, onReopenAd, adBranchIds, onAdd, total, onLoadMore, loadingMore }) {
  const colCount = (showBranch ? 11 : 10) + 1; // +1 for checkbox column
  if (loading) {
    return (
      <tr>
        <td colSpan={colCount} className="px-4 py-8 text-center font-mono text-xs text-ink-subtle">
          Loading…
        </td>
      </tr>
    );
  }
  if (vehicles.length === 0) {
    return (
      <tr>
        <td colSpan={colCount} className="px-4 py-12 text-center">
          <div className="font-mono text-xs text-ink-subtle mb-3">
            No vehicles match your filters.
          </div>
          <button onClick={onAdd} className="text-sm font-semibold text-brand hover:underline">
            + Add your first vehicle
          </button>
        </td>
      </tr>
    );
  }
  const hasMore = total > vehicles.length;
  return (
    <>
      {vehicles.map((v) => (
        <VehicleRow
          key={v.id}
          vehicle={v}
          packageMap={packageMap}
          branchMap={branchMap}
          showBranch={showBranch}
          isAdmin={isAdmin}
          selected={selectedIds.has(v.id)}
          onToggleSelect={onToggleSelect}
          onEdit={onEdit}
          onDelete={onDelete}
          onCloseAd={onCloseAd}
          onReopenAd={onReopenAd}
          isAdBranch={adBranchIds?.has(v.branch_id)}
        />
      ))}
      {hasMore && (
        <tr>
          <td colSpan={colCount} className="px-4 py-6 text-center bg-canvas-panel/40">
            <button
              type="button"
              onClick={onLoadMore}
              disabled={loadingMore}
              data-testid="inventory-load-more"
              className="px-4 py-2 border border-canvas-line text-xs font-mono uppercase tracking-wider hover:bg-canvas-panel disabled:opacity-50"
            >
              {loadingMore ? "Loading…" : `Load more · showing ${vehicles.length} of ${total}`}
            </button>
          </td>
        </tr>
      )}
      {!hasMore && total > 100 && (
        <tr>
          <td colSpan={colCount} className="px-4 py-3 text-center bg-canvas-panel/20 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-subtle">
            End of stockpile · {total} vehicles
          </td>
        </tr>
      )}
    </>
  );
}

export default function InventoryPage() {
  const { user } = useAuth();
  const { byId: branchMap } = useBranches();
  const [vehicles, setVehicles] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterBranch, setFilterBranch] = useState("");
  const [filterDealerCode, setFilterDealerCode] = useState("");
  const [filterModel, setFilterModel] = useState("");
  const [dealerCodes, setDealerCodes] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [showTransfer, setShowTransfer] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyRefresh, setHistoryRefresh] = useState(0);
  // Set of branch ids flagged as sub-dealer (AD) — used to show the
  // "Close (Sold by AD)" action only where it makes sense. Loaded once
  // on mount; if branch types change mid-session a page refresh picks
  // them up.
  const [adBranchIds, setAdBranchIds] = useState(() => new Set());
  useEffect(() => {
    api.get("/sub-dealers/ad-branch-ids")
      .then((r) => setAdBranchIds(new Set(r.data?.branch_ids || [])))
      .catch(() => setAdBranchIds(new Set()));
  }, []);
  // Server-side pagination: hold one slice at a time and grow the window
  // via "Load more". Keeps huge inventories (~5 000 rows on prod) from
  // choking the browser on every filter change. KPI tiles now consume a
  // dedicated status-counts endpoint so the numbers reflect the full
  // filtered set, not just the currently-loaded page.
  const PAGE_SIZE = 100;
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [statusCounts, setStatusCounts] = useState({ total: 0, by_status: {}, by_type: {} });

  const packageMap = useMemo(() => {
    const map = {};
    packages.forEach((p) => {
      map[p.id] = p;
    });
    return map;
  }, [packages]);

  const buildListParams = useCallback(() => {
    const params = {};
    if (filterType) params.type = filterType;
    if (filterStatus === "all") {
      params.status = "all";
    } else if (filterStatus) {
      params.status = filterStatus;
    }
    if (filterBranch) params.branch_id = filterBranch;
    if (filterDealerCode) params.dealer_code = filterDealerCode;
    if (filterModel) params.model_category = filterModel;
    if (search) params.search = search;
    return params;
  }, [filterType, filterStatus, filterBranch, filterDealerCode, filterModel, search]);

  const load = useCallback(async () => {
    setLoading(true);
    const listParams = { ...buildListParams(), limit: PAGE_SIZE, skip: 0 };
    // status-counts uses the same filters minus `status` (KPI tiles show
    // every bucket, not just the currently-filtered one).
    const countsParams = { ...buildListParams() };
    delete countsParams.status;
    try {
      const [vRes, pRes, dcRes, cRes] = await Promise.all([
        api.get("/vehicles", { params: listParams }),
        api.get("/packages"),
        api.get("/vehicles/dealer-codes"),
        api.get("/vehicles/status-counts", { params: countsParams }),
      ]);
      setVehicles(vRes.data);
      const t = Number(vRes.headers?.["x-total-count"]);
      setTotal(Number.isFinite(t) ? t : vRes.data.length);
      setPackages(pRes.data || []);
      setDealerCodes(dcRes.data?.codes || []);
      setStatusCounts(cRes.data || { total: 0, by_status: {}, by_type: {} });
    } finally {
      setLoading(false);
    }
  }, [buildListParams]);

  const loadMore = useCallback(async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const params = { ...buildListParams(), limit: PAGE_SIZE, skip: vehicles.length };
      const res = await api.get("/vehicles", { params });
      setVehicles((prev) => [...prev, ...res.data]);
      const t = Number(res.headers?.["x-total-count"]);
      if (Number.isFinite(t)) setTotal(t);
    } finally {
      setLoadingMore(false);
    }
  }, [buildListParams, vehicles.length, loadingMore]);

  useEffect(() => {
    load();
  }, [filterType, filterStatus, filterBranch, filterDealerCode, load]);

  const openCreate = () => {
    setEditing(null);
    setShowForm(true);
  };
  const openEdit = (v) => {
    setEditing(v);
    setShowForm(true);
  };
  const handleSaved = () => {
    setShowForm(false);
    load();
  };
  const handleDelete = async (v) => {
    if (!window.confirm(`Delete ${vehicleLabel(v) || v.vin}?`)) return;
    try {
      await api.delete(`/vehicles/${v.id}`);
      toast.success("Vehicle deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleCloseAd = async (v) => {
    const branchName = branchMap[v.branch_id]?.name || "this AD";
    if (!window.confirm(`Close ${v.vin} — mark as sold by ${branchName}?\n\nThis removes it from AD available stock. No accounting entry is created.`)) return;
    try {
      await api.post(`/vehicles/${v.id}/close-ad-sale`);
      toast.success("Closed as sold by AD");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleReopenAd = async (v) => {
    if (!window.confirm(`Reopen ${v.vin}? It will go back into AD available stock.`)) return;
    try {
      await api.post(`/vehicles/${v.id}/reopen-ad-sale`);
      toast.success("Reopened");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleSelect = (v) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(v.id)) next.delete(v.id);
      else next.add(v.id);
      return next;
    });
  };

  const selectableVehicles = vehicles.filter(
    (v) => v.status === "available" && !v.pending_transfer_id
  );
  const allVisibleSelected =
    selectableVehicles.length > 0 &&
    selectableVehicles.every((v) => selectedIds.has(v.id));

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableVehicles.map((v) => v.id)));
    }
  };

  const selectedVehicles = vehicles.filter((v) => selectedIds.has(v.id));

  const handleTransferDone = () => {
    setShowTransfer(false);
    setSelectedIds(new Set());
    setHistoryRefresh((n) => n + 1);
    load();
    toast.success("Inventory list refreshed");
  };

  return (
    <div>
      <PageHeader
        eyebrow="01 / Inventory"
        title="Vehicle Stockpile"
        subtitle="Manage cars, bikes and trucks across the entire dealership floor."
        actions={
          <div className="flex items-center gap-2">
            {user?.role === "admin" && (
              <button
                onClick={async () => {
                  if (!window.confirm(
                    "Remap each vehicle's packages to match its branch type? Safe to run repeatedly."
                  )) return;
                  try {
                    const { data } = await api.post("/vehicles/admin/remap-packages-by-branch-type");
                    toast.success(
                      `Scanned ${data.scanned} vehicles · Fixed ${data.fixed} package links.`
                    );
                    load();
                  } catch (err) {
                    toast.error(formatApiError(err));
                  }
                }}
                data-testid="remap-packages-btn"
                className="px-3 py-2.5 border border-canvas-line text-sm font-mono uppercase tracking-wider hover:border-ink"
                title="Reassign each vehicle's package_ids to its correct branch type's pricelist"
              >
                Fix Pricelists
              </button>
            )}
            <button
              onClick={() => setShowHistory(true)}
              data-testid="open-transfer-history"
              className="px-3 py-2.5 border border-canvas-line text-sm font-mono uppercase tracking-wider hover:border-ink flex items-center gap-2"
              title="View transfer history"
            >
              <ClockCounterClockwise size={13} weight="bold" />
              Log
            </button>
            <VehicleCsvIO
              onImported={load}
              canImport={user?.role === "admin" || (user?.permissions || []).includes("inventory.create")}
              filters={{
                ...(filterType ? { type: filterType } : {}),
                ...(filterStatus ? { status: filterStatus } : {}),
                ...(filterBranch ? { branch_id: filterBranch } : {}),
                ...(filterDealerCode ? { dealer_code: filterDealerCode } : {}),
                ...(search ? { search } : {}),
              }}
            />
            <button
              onClick={openCreate}
              data-testid="add-vehicle-button"
              className="bg-ink text-white px-4 py-2.5 text-sm font-semibold tracking-tight hover:bg-brand transition-colors duration-150 flex items-center gap-2"
            >
              <Plus size={14} weight="bold" />
              Add Vehicle
            </button>
          </div>
        }
      />

      <div className="px-8 py-6">
        <InventoryFilters
          search={search}
          setSearch={setSearch}
          filterType={filterType}
          setFilterType={setFilterType}
          filterStatus={filterStatus}
          setFilterStatus={setFilterStatus}
          filterBranch={filterBranch}
          setFilterBranch={setFilterBranch}
          filterDealerCode={filterDealerCode}
          setFilterDealerCode={setFilterDealerCode}
          filterModel={filterModel}
          setFilterModel={setFilterModel}
          dealerCodes={dealerCodes}
          onSearchSubmit={load}
        />

        {/* KPI tiles — counts across the entire filtered set (not just the
            currently-loaded page). Fed by /vehicles/status-counts. */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4" data-testid="inventory-kpis">
          {(() => {
            const byStatus = statusCounts.by_status || {};
            const byType = statusCounts.by_type || {};
            const tile = (label, value, accent) => (
              <div key={label} className={`border-2 ${accent || "border-canvas-line"} p-3 bg-canvas-card`}>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">{label}</div>
                <div className="font-display text-2xl font-bold tabular-nums">{value}</div>
              </div>
            );
            return [
              tile("Total", statusCounts.total || 0, "border-ink"),
              tile("Available", byStatus.available || 0, "border-[#00B341]"),
              tile("Reserved", byStatus.reserved || 0, "border-amber-500"),
              tile("Sold", byStatus.sold || 0),
              tile("Registered", byStatus.registered || 0, "border-sky-600"),
              tile("Cars / Bikes / Trucks", `${byType.car || 0} / ${byType.bike || 0} / ${byType.truck || 0}`),
            ];
          })()}
        </div>

        <div className="bg-canvas-card border border-canvas-line overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="inventory-table">
              <thead>
                <tr className="border-b border-canvas-line bg-canvas-panel">
                  <th className="text-left px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      data-testid="select-all-vehicles"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAll}
                      disabled={selectableVehicles.length === 0}
                      aria-label="Select all available vehicles"
                      title="Select all available vehicles"
                      className="cursor-pointer disabled:cursor-not-allowed"
                    />
                  </th>
                  {[
                    "Type",
                    "Vehicle",
                    "VIN",
                    "Dealer Code",
                    ...(user?.role === "admin" && !filterBranch ? ["Branch"] : []),
                    "Purchase Cost",
                    "Customer Price",
                    "Status",
                    "Stock Age",
                    "",
                  ].map((h) => (
                    <th
                      key={h}
                      className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <InventoryTableBody
                  loading={loading}
                  vehicles={vehicles}
                  packageMap={packageMap}
                  branchMap={branchMap}
                  showBranch={user?.role === "admin" && !filterBranch}
                  isAdmin={user?.role === "admin"}
                  selectedIds={selectedIds}
                  onToggleSelect={toggleSelect}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                  onCloseAd={handleCloseAd}
                  onReopenAd={handleReopenAd}
                  adBranchIds={adBranchIds}
                  onAdd={openCreate}
                  total={total}
                  onLoadMore={loadMore}
                  loadingMore={loadingMore}
                />
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <VehicleFormDrawer
        open={showForm}
        editing={editing}
        onClose={() => setShowForm(false)}
        onSaved={handleSaved}
      />

      {/* Floating action bar — appears when ≥1 row selected */}
      {selectedVehicles.length > 0 && (
        <div
          data-testid="transfer-action-bar"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-ink text-white px-5 py-3 shadow-2xl flex items-center gap-4 border border-ink"
        >
          <span className="text-sm font-semibold">
            {selectedVehicles.length} vehicle{selectedVehicles.length === 1 ? "" : "s"} selected
          </span>
          <button
            data-testid="clear-selection"
            onClick={() => setSelectedIds(new Set())}
            className="text-xs font-mono uppercase tracking-wider text-white/70 hover:text-white"
          >
            Clear
          </button>
          <button
            data-testid="open-transfer-dialog"
            onClick={() => setShowTransfer(true)}
            className="bg-white text-ink px-4 py-2 text-sm font-semibold hover:bg-brand hover:text-white transition-colors flex items-center gap-2"
          >
            <ArrowsLeftRight size={13} weight="bold" />
            Transfer to branch
          </button>
        </div>
      )}

      <VehicleTransferDialog
        open={showTransfer}
        vehicles={selectedVehicles}
        onClose={() => setShowTransfer(false)}
        onDone={handleTransferDone}
      />

      <TransferHistoryDrawer
        open={showHistory}
        refreshKey={historyRefresh}
        onClose={() => setShowHistory(false)}
      />
    </div>
  );
}
